import React, { lazy, useEffect, useState, Suspense } from 'react';
import { Route, Routes } from 'react-router-dom';
import css from './app.scss';
import LandingPage from './components/landing/landing-page';
import { useLoadData } from './utils/useLoadData';
import { getUser, getCustomerAlertBox, setFeatures } from './redux/actions/appActions';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import ErrorBoundary from './components/core/error-boundary';
import Permission from './components/core/permission';
import Dashboard from './components/dashboard/dashboard';
import LocalAuth from '@shamrock-core/common/authentication/local-authentication';
import Fallback from './components/core/fallback';
import FirebaseAnalytics from './components/core/analytics';
import 'shamrock-clover-ui/dist/clover/css/clover.css';
import { Snackbar } from 'shamrock-clover-ui';
import { featureService } from './services/feature.service';
import CustomerAlertBox from './components/core/CustomerAlertBox';
import type { RootState } from 'bootstrap';
import type { CustomerAlertBoxState } from 'redux/reducers/appReducer';
import * as Airship from './components/core/airship';
import { FederatedModules } from './types';
import { usePardotUrl } from './utils/usePardotUrl';
import { createDeviceInfo } from './redux/thunks/appThunks';
import { Settings } from './components/settings/settings';
import {
  getUserId,
  hasCreditPermission,
  hasFactoringPermission,
  hasFreightPermission,
  hasFuelPermission,
  hasBankingPermission,
  hasLoadManagementPermission,
  hasTripManagementPermission,
  hasProTransportPremiumPermission,
  isInternalUser,
  hasProTransportBasicPermission,
} from './utils/glassboxUtils';
import PasswordReset from './components/login/PasswordReset';
import { useTranslation } from 'react-i18next';
import RTSProAnniversary from './components/core/rtsproAnniversary';
import TermsAndConditionsModal from './components/core/TermsAndConditionsModal';
import { TrialPopupModal } from './components/TrialPopupModal/TrialPopupModal';
import SideNavigation from './components/headers/side-navigation';
import classNames from 'classnames';
import TopMenu from './components/headers/top-menu';
import FactoringClientSelector from './components/headers/factoring-client-selector';
import { useAuth0 } from '@auth0/auth0-react';
import ProtectedComponent from './components/core/ProtectedComponent';
import { FF } from './utils/utils';
import PtPremiumBanner from './components/proTransportSubscriptions/components/ptPremiumBanner';
import { addAuth0Context } from './utils/rtk-query';
import { useCanViewPTSubscriptions } from './components/proTransportSubscriptions/hooks/permissions';

const TMS = lazy(() => import('tms/TMSApp'));
const Credit = lazy(() => import('credit/CreditApp'));
const Fuel = lazy(() => import('fuel/FuelApp'));
const Freight = lazy(() => import('freight/FreightApp'));
const Factoring = lazy(() => import('factoring/FactoringApp'));
const Banking = lazy(() => import('banking/BankingApp'));

const App = () => {
  const { i18n } = useTranslation();
  const dispatch = useDispatch();
  const [loading, setLoading] = React.useState(true);
  const features = useSelector<RootState, string[]>((state) => state.app.features);
  const user = useSelector(getUser, shallowEqual);
  const permissions = useSelector<RootState, string[]>((state) => state.app.permissions) || user?.permissions;
  const [_rerender, setRerender] = useState<number>(0);
  const { error: loadDataError, loading: loadingData } = useLoadData();
  const {
    isAuthenticated: isAuthenticatedAuth0,
    isLoading: isLoadingAuth0,
    logout,
    getAccessTokenSilently,
  } = useAuth0();
  const cpua5206 = features.includes('CPUA-5206');
  const isAuth0Enabled = features.includes(FF.AUTH0);
  const isBankingUser = features.includes(FF.BANKING) || features.includes(FF.DIGITAL_BANKING);
  const loadManagmentUrl = usePardotUrl({
    url: `https://www.rtsinc.com/lp/rtspro-load-management`,
    product: FederatedModules.LOAD_MANAGEMENT,
  });
  const creditUrl = usePardotUrl({ url: `https://www.rtsinc.com/lp/rtspro-credit`, product: FederatedModules.CREDIT });
  const fuelUrl = usePardotUrl({ url: `https://www.rtsinc.com/lp/rtspro-fuel`, product: FederatedModules.FUEL });
  const freightUrl = usePardotUrl({
    url: `https://www.ryantrans.com/landing/rtspro-freight`,
    product: FederatedModules.FREIGHT,
  });
  const factoringUrl = usePardotUrl({
    url: `https://www.rtsinc.com/lp/rtspro-factoring`,
    product: FederatedModules.FACTORING,
  });
  const bankingUrl = usePardotUrl({
    url: `https://www.rtsinc.com/lp/rtspro-banking`,
    product: FederatedModules.BANKING,
  });
  const { t } = useTranslation();

  const { visible, title, description } = useSelector<RootState, CustomerAlertBoxState>(
    getCustomerAlertBox,
    shallowEqual,
  );
  const [showRTSAnniversary, setShowRTSAnniversary] = useState(true);
  const isRTSAnniversaryFeatureFlagEnabled = features.includes('CPUA-3585-WEB');
  const isTermsAndConditionsModalEnabled = features.includes('CPUA-914-WEB');
  const tmsBasicTrialEnabled = features.includes('CPUA-4769-WEB');
  const useUpdatedSupportNumber = features.includes('fe0aef84a5006df196d7a66b7d1feddbc2bca483010aeb37251494600ebb59bf');
  const canViewPTSubscriptions = useCanViewPTSubscriptions();
  const isEligibleForPTUpgrade = canViewPTSubscriptions && hasProTransportBasicPermission(permissions);

  const activeAccount = useSelector<RootState>((state) => state.app.imUser?.active_account);
  //Remove loadManagement.User string when we are ready to switch off of it
  const tmsUserPermissions = ['tms.user.enterprise', 'tms.user.premium', 'tms.user.basic', 'loadManagement.User'];

  let isAuthenticated;

  useEffect(() => {
    try {
      Airship.registerBrowser();
    } catch (e) {
      console.error(e);
    }
    featureService.getAllActiveFeatures().then((res) => {
      dispatch(setFeatures(res ?? []));
    });

    featureService
      .getFeatures(LocalAuth.getUser().email as string)
      .then((features) => {
        dispatch(setFeatures(features));
      })
      .then(() => {
        setLoading(false);
      })
      .catch((e) => {
        console.error('Error on featured flag: ', e);
        setLoading(false);
      });
  }, []);

  useEffect(() => {
    try {
      if (user) {
        Airship.setNamedUser(user._id);
        FirebaseAnalytics.setUser(user._id);
      }
    } catch (e) {
      console.error(e);
    }
  }, [user?._id]);

  useEffect(() => {
    if (isAuthenticated && user?.userStatus?.isRegistered && cpua5206) {
      dispatch(createDeviceInfo(isAuthenticatedAuth0));
    }
  }, [isAuthenticated, user?.userStatus, cpua5206]);

  useEffect(() => {
    try {
      if (isAuthenticated && user) {
        addAuth0Context(getAccessTokenSilently, isAuthenticated, logout);

        const _detector = global._detector;
        if (!_detector) return;

        const userMap = {
          userId: getUserId(user),
          isInternal: isInternalUser(user),
        };
        const permissionsMap = {
          hasFuel: hasFuelPermission(permissions),
          hasFreight: hasFreightPermission(permissions),
          hasFactoring: hasFactoringPermission(permissions),
          hasCredit: hasCreditPermission(permissions),
          hasLoadManagement: hasLoadManagementPermission(permissions),
          hasTripManagement: hasTripManagementPermission(permissions),
          hasProTransportBasic: hasProTransportBasicPermission(permissions),
          hasProTransportPremium: hasProTransportPremiumPermission(permissions),
          hasBankingPermission: hasBankingPermission(permissions),
        };

        _detector.triggerCustomEventMap('user', userMap);
        _detector.triggerCustomEventMap('permissions', permissionsMap);
      }
    } catch (error: any) {
      console.error(error);
    }
  }, [isAuthenticated, user, permissions]);

  try {
    isAuthenticated = !!LocalAuth.checkSession();
  } catch (e) {
    isAuthenticated = !!isAuthenticatedAuth0 || false;
  }

  if ((loading || (isAuth0Enabled && isLoadingAuth0)) && !isAuthenticated) {
    return <Fallback />;
  }

  return (
    <div className={classNames(css.appContainer, isAuthenticated && user ? css.flexRow : css.flexColumn)}>
      {isAuthenticated && user ? (
        <>
          {showRTSAnniversary && isRTSAnniversaryFeatureFlagEnabled ? (
            <RTSProAnniversary showAnniversaryStateChanger={setShowRTSAnniversary} />
          ) : (
            <>
              {<SideNavigation />}
              {visible && <CustomerAlertBox title={title} description={description} />}
              {isTermsAndConditionsModalEnabled && <TermsAndConditionsModal />}
              {tmsBasicTrialEnabled && <TrialPopupModal />}
              <div className={css.content}>
                <Suspense fallback={<Fallback />}>
                  <Routes>
                    <Route
                      path="/loads/*"
                      key={'loads'}
                      element={
                        <ProtectedComponent>
                          <ErrorBoundary useUpdatedSupportNumber={useUpdatedSupportNumber}>
                            <Permission
                              permission={tmsUserPermissions}
                              module={FirebaseAnalytics.MODULES.LOADS}
                              fallback={loadManagmentUrl}
                              withActiveTrial={tmsBasicTrialEnabled}
                            >
                              <TMS
                                features={features}
                                i18n={i18n}
                                clientNumber={activeAccount}
                                factoringClientSelector={<FactoringClientSelector />}
                                header={<TopMenu />}
                              />
                            </Permission>
                          </ErrorBoundary>
                        </ProtectedComponent>
                      }
                    />
                    <Route
                      path="/credit/*"
                      key={'credit'}
                      element={
                        <ProtectedComponent>
                          <ErrorBoundary useUpdatedSupportNumber={useUpdatedSupportNumber}>
                            <Permission
                              permission={['credit.User', 'invoicemanager.User', 'factoring.User', 'trial.User']}
                              module={FirebaseAnalytics.MODULES.CREDIT}
                              fallback={creditUrl}
                            >
                              <Credit
                                i18n={i18n}
                                clientNumber={activeAccount}
                                history={history}
                                header={<TopMenu />}
                                factoringClientSelector={<FactoringClientSelector />}
                              />
                            </Permission>
                          </ErrorBoundary>
                        </ProtectedComponent>
                      }
                    />
                    <Route
                      path="/fuel/*"
                      key={'fuel'}
                      element={
                        <ProtectedComponent>
                          <ErrorBoundary useUpdatedSupportNumber={useUpdatedSupportNumber}>
                            <Permission
                              permission={'fuel.User'}
                              module={FirebaseAnalytics.MODULES.FUEL}
                              fallback={fuelUrl}
                            >
                              <Fuel i18n={i18n} header={<TopMenu />} />
                            </Permission>
                          </ErrorBoundary>
                        </ProtectedComponent>
                      }
                    />
                    <Route
                      path="/freight/*"
                      key={'freight'}
                      element={
                        <ProtectedComponent>
                          <ErrorBoundary useUpdatedSupportNumber={useUpdatedSupportNumber}>
                            <Permission
                              permission={'ryantrans.Loadboard'}
                              module={FirebaseAnalytics.MODULES.FREIGHT}
                              fallback={freightUrl}
                            >
                              <Freight i18n={i18n} header={<TopMenu />} />
                            </Permission>
                          </ErrorBoundary>
                        </ProtectedComponent>
                      }
                    />

                    <Route
                      path="/factoring/*"
                      key={'factoring'}
                      element={
                        <ProtectedComponent>
                          <ErrorBoundary useUpdatedSupportNumber={useUpdatedSupportNumber}>
                            <Permission
                              permission={'invoicemanager.User'}
                              module={FirebaseAnalytics.MODULES.FACTORING}
                              fallback={factoringUrl}
                            >
                              <Factoring
                                i18n={i18n}
                                clientNumber={activeAccount}
                                header={<TopMenu />}
                                factoringClientSelector={<FactoringClientSelector />}
                              />
                            </Permission>
                          </ErrorBoundary>
                        </ProtectedComponent>
                      }
                    />
                    {isBankingUser && (
                      <Route
                        path="/banking/*"
                        key={'banking'}
                        element={
                          <ProtectedComponent>
                            <ErrorBoundary useUpdatedSupportNumber={useUpdatedSupportNumber}>
                              <Permission
                                permission={['banking.User', 'digitalbankingdebit.User']}
                                module={FirebaseAnalytics.MODULES.BANKING}
                                fallback={bankingUrl}
                              >
                                <Banking
                                  i18n={i18n}
                                  clientNumber={activeAccount}
                                  header={<TopMenu />}
                                  factoringClientSelector={<FactoringClientSelector />}
                                />
                              </Permission>
                            </ErrorBoundary>
                          </ProtectedComponent>
                        }
                      />
                    )}
                    <Route
                      path="/settings/"
                      key={'settings'}
                      element={
                        <ProtectedComponent>
                          <ErrorBoundary useUpdatedSupportNumber={useUpdatedSupportNumber}>
                            <div className={css.secondaryNavigation}>
                              <TopMenu />
                            </div>
                            <Settings />
                          </ErrorBoundary>
                        </ProtectedComponent>
                      }
                    />

                    <Route
                      path="/*"
                      key={'dashboard'}
                      element={
                        <ProtectedComponent>
                          <ErrorBoundary useUpdatedSupportNumber={useUpdatedSupportNumber}>
                            <Dashboard />
                          </ErrorBoundary>
                        </ProtectedComponent>
                      }
                    />
                  </Routes>
                </Suspense>
              </div>
            </>
          )}
        </>
      ) : loadingData ? (
        <Fallback />
      ) : (
        !isAuthenticated && (
          <Routes>
            <Route path="/*" key={'root'} element={<LandingPage setRerender={setRerender} />} />
          </Routes>
        )
      )}
      <Routes>
        <Route
          path="/password-reset"
          key={'passwordReset'}
          element={
            <ErrorBoundary useUpdatedSupportNumber={useUpdatedSupportNumber}>
              <PasswordReset />
            </ErrorBoundary>
          }
        />
      </Routes>
      <Snackbar
        open={!!loadDataError}
        duration={180000}
        message={t('something_went_wrong_please_try_again_later')}
        variant="error"
        position="topCenter"
      />
      {isEligibleForPTUpgrade && <PtPremiumBanner />}
    </div>
  );
};

export default App;
